import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { authApi } from '@/services/authService';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Wallet,
  TrendingUp,
  Receipt,
  Clock,
  Search,
  Download,
  ArrowRight,
  Banknote,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Building2,
  Mail,
  KeyRound,
  ArrowUpDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface PayoutRequest {
  _id: string;
  id_payout: string;
  email: string;
  status: 'requested' | 'processing' | 'paid' | 'failed' | 'pending' | 'canceled';
  amount: number;
  createdAt: string;
  paymentAccount?: {
    type: 'bank' | 'pix' | 'paypal';
    key_pix?: string;
    key_paypal?: string;
    bank_number?: string;
    account_bank_number?: string;
    name_user_account?: string;
  } | null;
}

interface AdminSummaryData {
  totalSolicited: number;
  totalAmount: number;
  totalTaxes: number;
  requests: PayoutRequest[];
}

type SortField = 'email' | 'status' | 'amount' | 'createdAt';
type SortDirection = 'ASC' | 'DESC';

const formatBRL = (v: number | undefined) =>
  (typeof v === 'number' ? v : 0).toLocaleString(getInterfaceLanguage(), {
    style: 'currency',
    currency: 'BRL',
  });

const statusStyles: Record<string, { cls: string; dot: string; label: string }> = {
  requested: {
    cls: 'bg-amber-50 text-amber-700 border-amber-100',
    dot: 'bg-amber-500',
    get label() { return translateInterfaceText("Solicitado"); },
  },
  pending: {
    cls: 'bg-orange-50 text-orange-700 border-orange-100',
    dot: 'bg-orange-500',
    get label() { return translateInterfaceText("Pendente"); },
  },
  processing: {
    cls: 'bg-blue-50 text-blue-700 border-blue-100',
    dot: 'bg-blue-500',
    get label() { return translateInterfaceText("Processando"); },
  },
  paid: {
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    dot: 'bg-emerald-500',
    get label() { return translateInterfaceText("Pago"); },
  },
  failed: {
    cls: 'bg-red-50 text-red-700 border-red-100',
    dot: 'bg-red-500',
    get label() { return translateInterfaceText("Falhou"); },
  },
  canceled: {
    cls: 'bg-gray-100 text-gray-600 border-gray-200',
    dot: 'bg-gray-400',
    get label() { return translateInterfaceText("Cancelado"); },
  },
};

interface KpiProps {
  icon: React.ElementType;
  label: string;
  value: string;
  description?: string;
  badge?: string;
}

const KpiCard: React.FC<KpiProps> = ({ icon: Icon, label, value, description, badge }) => { useLanguage(); return ((
  <Card className="p-5 rounded-2xl border border-slate-200 shadow-none bg-white">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[12.5px] font-medium text-[#6B7280] leading-tight">{label}</p>
          {badge && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-semibold">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-[22px] font-semibold text-[#0A1A2F] leading-none tracking-tight">
          {value}
        </p>
        {description && (
          <p className="mt-1.5 text-[11.5px] text-[#9CA3AF] leading-snug">{description}</p>
        )}
      </div>
    </div>
  </Card>
)); };

export default function AdminSummary() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState<AdminSummaryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Filtros e paginação
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [perPage, setPerPage] = useState<string>('10');
  const [currentPage, setCurrentPage] = useState(1);

  // Ordenação
  const [orderBy, setOrderBy] = useState<SortField>('createdAt');
  const [orderDirection, setOrderDirection] = useState<SortDirection>('DESC');

  // Modal
  const [selected, setSelected] = useState<PayoutRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Guard admin
  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita exclusivamente para administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchAdminSummary = useCallback(async () => {
    if (!user || user.nivel !== 'admin') return;
    try {
      setLoading(true);
      const response = await authApi.get('/stripe/afiliate/finance/admin/balance-summary');
      if (response.data && response.data.success) {
        setMetrics({
          totalSolicited: Number(response.data.TotalPaymentResquests.totalSolicited || 0),
          totalAmount: Number(response.data.TotalPaymentResquests.totalAmount || 0),
          totalTaxes: Number(response.data.TotalPaymentResquests.totalTaxes || 0),
          requests: response.data.valuations.data || [],
        });
      }
    } catch (err: any) {
      toast.error(translateInterfaceText("Erro ao coletar balanço de auditoria."));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAdminSummary();
  }, [fetchAdminSummary]);

  // Toggle de ordenação ao clicar no cabeçalho
  const handleSort = (field: SortField) => {
    if (orderBy === field) {
      setOrderDirection(orderDirection === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setOrderBy(field);
      setOrderDirection('DESC');
    }
  };

  // Filtragem + ordenação
  const filteredAndSorted = useMemo(() => {
    if (!metrics?.requests) return [];

    let result = metrics.requests.filter((r) => {
      const matchSearch = !search || r.email.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchSearch && matchStatus;
    });

    result = [...result].sort((a, b) => {
      let valA: any = a[orderBy];
      let valB: any = b[orderBy];

      if (orderBy === 'createdAt') {
        valA = new Date(a.createdAt).getTime();
        valB = new Date(b.createdAt).getTime();
      }

      return orderDirection === 'ASC' ? (valA > valB ? 1 : -1) : valA < valB ? 1 : -1;
    });

    return result;
  }, [metrics?.requests, search, statusFilter, orderBy, orderDirection]);

  const limit = Number(perPage);
  const totalPages = Math.ceil(filteredAndSorted.length / limit) || 1;
  const paginated = filteredAndSorted.slice((currentPage - 1) * limit, currentPage * limit);

  // Resetar página ao alterar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, perPage]);

  const openModal = (r: PayoutRequest) => {
    setSelected(r);
    setIsModalOpen(true);
  };

  const handleExportCSV = () => {
    if (!metrics?.requests || metrics.requests.length === 0) {
      toast.error(translateInterfaceText("Não há dados para exportar."));
      return;
    }

    const headers = ['Email', 'Status', 'Valor', 'Data'];
    const rows = metrics.requests.map((r) => [
      r.email,
      statusStyles[r.status]?.label || r.status,
      formatBRL(r.amount),
      new Date(r.createdAt).toLocaleDateString(getInterfaceLanguage()),
    ]);

    const csv = [headers, ...rows].map((row) => row.join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extrato-afiliados-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(translateInterfaceText("Exportação concluída com sucesso!"));
  };

  // Helpers de conta de pagamento
  const getAccountTypeLabel = (type?: string) => {
    if (type === 'bank') return translateInterfaceText("Conta Bancária");
    if (type === 'pix') return 'PIX';
    if (type === 'paypal') return 'PayPal';
    return '—';
  };

  const getAccountSummary = (req: PayoutRequest) => {
    if (!req.paymentAccount) return { type: '—', detail: translateInterfaceText("Não configurada") };
    const t = req.paymentAccount.type;
    if (t === 'bank') return { type: 'Banco', detail: req.paymentAccount.name_user_account || '—' };
    if (t === 'pix') return { type: 'PIX', detail: req.paymentAccount.key_pix || '—' };
    if (t === 'paypal') return { type: 'PayPal', detail: req.paymentAccount.key_paypal || '—' };
    return { type: '—', detail: '—' };
  };

  // Contagem de pendentes
  const pendingCount = useMemo(() => {
    if (!metrics?.requests) return 0;
    return metrics.requests.filter((r) => r.status === 'requested' || r.status === 'pending')
      .length;
  }, [metrics?.requests]);

  if (!user || user.nivel !== 'admin') return null;

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <div className="[&_.desktop-header]:!border-b-[#D1D5DB]">
          <Header title={translateInterfaceText("Extrato Administrativo")} />
        </div>

        <main className="flex-1 overflow-y-auto scrollbar-blue">
          <div className="max-w-6xl mx-auto p-6 md:p-8 space-y-7">
            {/* Subheadline + Ações */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <p className="text-[13px] text-[#6B7280] leading-relaxed max-w-2xl">
                  {translateInterfaceText("Gerencie pagamentos, solicitações e repasses da rede de afiliados.")}</p>
              </div>
              <div className="flex items-center gap-2.5">
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  className="h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-[#F3F4F6] hover:text-[#111827] gap-2"
                >
                  <Download className="w-4 h-4" />
                  {translateInterfaceText("Exportar CSV")}</Button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                icon={Wallet}
                label={translateInterfaceText("Total de Comissões")}
                value={formatBRL(metrics?.totalAmount)}
                description={translateInterfaceText("Acumulado por todos os afiliados.")}
              />
              <KpiCard
                icon={TrendingUp}
                label={translateInterfaceText("Valor Líquido")}
                value={formatBRL(metrics?.totalSolicited)}
                description={translateInterfaceText("Total após taxas aplicadas.")}
              />
              <KpiCard
                icon={Receipt}
                label={translateInterfaceText("Taxas Acumuladas")}
                value={formatBRL(metrics?.totalTaxes)}
                description={translateInterfaceText("Taxas cobradas sobre pagamentos.")}
              />
              <KpiCard
                icon={Clock}
                label={translateInterfaceText("Solicitações Pendentes")}
                value={String(pendingCount)}
                description={translateInterfaceText("Aguardando pagamento.")}
                badge={pendingCount > 0 ? 'Pendente' : undefined}
              />
            </div>

            {/* Tabela */}
            <Card className="rounded-2xl border border-slate-200 shadow-none bg-white overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-200">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-[15px] font-semibold text-[#0A1A2F]">
                      {translateInterfaceText("Solicitações de Pagamento")}</h2>
                    <p className="text-[12.5px] text-[#6B7280] mt-0.5">
                      {translateInterfaceText("Todas as solicitações enviadas pelos afiliados da plataforma.")}</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-2.5">
                  <div className="flex-1 flex items-center gap-2 px-3 h-10 rounded-xl border border-slate-200 bg-white">
                    <Search className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                    <Input
                      placeholder={translateInterfaceText("Buscar por email...")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-auto border-0 bg-transparent p-0 text-[13px] text-[#0A1A2F] placeholder:text-[#9CA3AF] focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-10 w-full md:w-[180px] rounded-xl border-slate-200 text-[13px] text-[#0A1A2F] bg-white shadow-none">
                      <SelectValue placeholder={translateInterfaceText("Todos Status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{translateInterfaceText("Todos Status")}</SelectItem>
                      <SelectItem value="requested">{translateInterfaceText("Solicitado")}</SelectItem>
                      <SelectItem value="pending">{translateInterfaceText("Pendente")}</SelectItem>
                      <SelectItem value="processing">{translateInterfaceText("Processando")}</SelectItem>
                      <SelectItem value="paid">{translateInterfaceText("Pago")}</SelectItem>
                      <SelectItem value="failed">{translateInterfaceText("Falhou")}</SelectItem>
                      <SelectItem value="canceled">{translateInterfaceText("Cancelado")}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={perPage} onValueChange={setPerPage}>
                    <SelectTrigger className="h-10 w-full md:w-[120px] rounded-xl border-slate-200 text-[13px] text-[#0A1A2F] bg-white shadow-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">{translateInterfaceText("10 p/ pág")}</SelectItem>
                      <SelectItem value="25">{translateInterfaceText("25 p/ pág")}</SelectItem>
                      <SelectItem value="50">{translateInterfaceText("50 p/ pág")}</SelectItem>
                      <SelectItem value="100">{translateInterfaceText("100 p/ pág")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[12px] text-[#6B7280] font-medium bg-slate-50">
                      <th
                        onClick={() => handleSort('email')}
                        className="px-6 py-3 cursor-pointer select-none hover:text-[#2563EB] transition-colors group"
                      >
                        <div className="flex items-center gap-1.5">
                          {translateInterfaceText("Usuário")}<ArrowUpDown
                            className={cn(
                              'w-3 h-3 transition-opacity',
                              orderBy === 'email'
                                ? 'text-[#2563EB] opacity-100'
                                : 'opacity-30 group-hover:opacity-60',
                            )}
                          />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        className="px-4 py-3 cursor-pointer select-none hover:text-[#2563EB] transition-colors group"
                      >
                        <div className="flex items-center gap-1.5">
                          {translateInterfaceText("Status")}<ArrowUpDown
                            className={cn(
                              'w-3 h-3 transition-opacity',
                              orderBy === 'status'
                                ? 'text-[#2563EB] opacity-100'
                                : 'opacity-30 group-hover:opacity-60',
                            )}
                          />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('amount')}
                        className="px-4 py-3 cursor-pointer select-none hover:text-[#2563EB] transition-colors group"
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          {translateInterfaceText("Valor")}<ArrowUpDown
                            className={cn(
                              'w-3 h-3 transition-opacity',
                              orderBy === 'amount'
                                ? 'text-[#2563EB] opacity-100'
                                : 'opacity-30 group-hover:opacity-60',
                            )}
                          />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('createdAt')}
                        className="px-4 py-3 cursor-pointer select-none hover:text-[#2563EB] transition-colors group"
                      >
                        <div className="flex items-center gap-1.5">
                          {translateInterfaceText("Data Solicitação")}<ArrowUpDown
                            className={cn(
                              'w-3 h-3 transition-opacity',
                              orderBy === 'createdAt'
                                ? 'text-[#2563EB] opacity-100'
                                : 'opacity-30 group-hover:opacity-60',
                            )}
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3">{translateInterfaceText("Conta")}</th>
                      <th className="px-6 py-3 text-right">{translateInterfaceText("Ações")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-20">
                          <div className="flex flex-col items-center justify-center text-center">
                            <div className="h-8 w-8 rounded-full border-4 border-[#2563EB] border-t-transparent animate-spin mb-4" />
                            <p className="text-[13px] text-[#6B7280]">
                              {translateInterfaceText("Carregando solicitações...")}</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginated.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-20">
                          <div className="flex flex-col items-center justify-center text-center">
                            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                              <Banknote className="w-6 h-6 text-[#2563EB]" />
                            </div>
                            <h3 className="text-[15px] font-semibold text-[#111827]">
                              {translateInterfaceText("Nenhuma solicitação encontrada")}</h3>
                            <p className="text-[13px] text-[#6B7280] mt-1 max-w-[420px]">
                              {translateInterfaceText("As solicitações de saque dos afiliados aparecerão aqui.")}</p>
                            <Button
                              variant="outline"
                              onClick={fetchAdminSummary}
                              className="mt-5 h-10 rounded-xl border-[#E5EAF3] bg-white hover:bg-[#F3F4F6] gap-2"
                            >
                              <RefreshCw className="w-4 h-4" />
                              {translateInterfaceText("Atualizar dados")}</Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginated.map((r) => {
                        const acc = getAccountSummary(r);
                        const statusInfo = statusStyles[r.status] || statusStyles.requested;
                        return (
                          <tr
                            key={r._id}
                            className="border-t border-slate-200 hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-6 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#EEF2FF] text-[#2563EB] flex items-center justify-center text-[12px] font-semibold">
                                  {r.email.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-[#0A1A2F] font-medium leading-tight">
                                    {r.email}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <Badge
                                variant="outline"
                                className={cn(
                                  'rounded-full px-2.5 py-0.5 font-medium gap-1.5',
                                  statusInfo.cls,
                                )}
                              >
                                <span
                                  className={cn('w-1.5 h-1.5 rounded-full', statusInfo.dot)}
                                />
                                {statusInfo.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3.5 text-right font-semibold text-[#0A1A2F]">
                              {formatBRL(r.amount)}
                            </td>
                            <td className="px-4 py-3.5 text-[#6B7280]">
                              {new Date(r.createdAt).toLocaleDateString(getInterfaceLanguage())}
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col">
                                <span className="text-[#0A1A2F] font-medium">{acc.type}</span>
                                <span className="text-[11.5px] text-[#9CA3AF] truncate max-w-[180px]">
                                  {acc.detail}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3.5 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openModal(r)}
                                className="h-8 rounded-lg text-[#2563EB] hover:bg-[#EEF2FF] hover:text-[#1D4ED8] gap-1.5 text-[12.5px] font-medium"
                              >
                                {translateInterfaceText("Ver Detalhes")}<ArrowRight className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {paginated.length > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <p className="text-[12px] text-[#6B7280]">
                    {translateInterfaceText("Total de")}{" "}{filteredAndSorted.length}{' '}
                    {filteredAndSorted.length === 1 ? translateInterfaceText("registro") : translateInterfaceText("registros")} {" "}{translateInterfaceText("· Página")}{' '}
                    {currentPage} {" "}{translateInterfaceText("de")}{" "}{totalPages}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      className="h-8 w-8 rounded-lg border-slate-200 text-[#6B7280] shadow-none disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 min-w-8 px-2 rounded-lg bg-blue-600 text-white hover:bg-blue-600 shadow-none text-[12px] font-semibold"
                    >
                      {currentPage}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      className="h-8 w-8 rounded-lg border-slate-200 text-[#6B7280] shadow-none disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </main>
      </div>

      {/* Modal Dados para Pagamento */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[560px] max-w-[95vw] rounded-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="px-6 pt-6 pb-5 space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                <Banknote className="w-[18px] h-[18px] text-blue-600" />
              </div>
              <DialogTitle className="text-[18px] font-bold text-[#111827]">
                {translateInterfaceText("Dados para Pagamento")}</DialogTitle>
            </div>
            <DialogDescription className="text-[13px] text-[#6B7280] pl-[46px]">
              {translateInterfaceText("Conta definida pelo usuário para recebimento.")}</DialogDescription>
          </DialogHeader>

          <div className="h-px bg-[#EEF0F3]" />

          <div className="px-6 py-5 space-y-5">
            {selected ? (
              <>
                {/* Valor destaque */}
                <div className="p-4 rounded-xl bg-white border border-slate-200">
                  <p className="text-[12px] font-medium text-[#4B5563]">{translateInterfaceText("Valor a ser pago")}</p>
                  <p className="text-[26px] font-bold text-[#0A1A2F] leading-tight mt-1">
                    {formatBRL(selected.amount)}
                  </p>
                  <p className="text-[12px] text-[#6B7280] mt-1">
                    {translateInterfaceText("Solicitado em")}{" "}{new Date(selected.createdAt).toLocaleDateString(getInterfaceLanguage())}
                  </p>
                </div>

                {/* Dados da conta */}
                {selected.paymentAccount ? (
                  <div className="space-y-3">
                    <p className="text-[13px] font-semibold text-[#0A1A2F]">{translateInterfaceText("Dados Bancários")}</p>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl border border-slate-200 bg-white">
                        <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Tipo de conta")}</p>
                        <p className="text-[13.5px] font-semibold text-[#0A1A2F] mt-0.5">
                          {getAccountTypeLabel(selected.paymentAccount.type)}
                        </p>
                      </div>
                      <div className="p-3 rounded-xl border border-slate-200 bg-white">
                        <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Email do usuário")}</p>
                        <p className="text-[13.5px] font-semibold text-[#0A1A2F] mt-0.5 truncate">
                          {selected.email}
                        </p>
                      </div>
                    </div>

                    {/* Conta Bancária */}
                    {selected.paymentAccount.type === 'bank' && (
                      <div className="space-y-3">
                        <div className="p-3 rounded-xl border border-slate-200 bg-white">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-3.5 h-3.5 text-[#6B7280]" />
                            <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Titular")}</p>
                          </div>
                          <p className="text-[13.5px] font-semibold text-[#0A1A2F]">
                            {selected.paymentAccount.name_user_account || translateInterfaceText("Não informado")}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 rounded-xl border border-slate-200 bg-white">
                            <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Cód. Banco")}</p>
                            <p className="text-[13.5px] font-semibold text-[#0A1A2F] mt-0.5">
                              {selected.paymentAccount.bank_number || '—'}
                            </p>
                          </div>
                          <div className="p-3 rounded-xl border border-slate-200 bg-white">
                            <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Agência/Conta")}</p>
                            <p className="text-[13.5px] font-semibold text-[#0A1A2F] mt-0.5 break-all">
                              {selected.paymentAccount.account_bank_number || '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PIX */}
                    {selected.paymentAccount.type === 'pix' && (
                      <div className="p-3 rounded-xl border border-slate-200 bg-white">
                        <div className="flex items-center gap-2 mb-1">
                          <KeyRound className="w-3.5 h-3.5 text-[#6B7280]" />
                          <p className="text-[11.5px] text-[#6B7280]">{translateInterfaceText("Chave PIX cadastrada")}</p>
                        </div>
                        <p className="text-[13.5px] font-semibold text-[#0A1A2F] break-all select-all">
                          {selected.paymentAccount.key_pix || '—'}
                        </p>
                      </div>
                    )}

                    {/* PayPal */}
                    {selected.paymentAccount.type === 'paypal' && (
                      <div className="p-3 rounded-xl border border-slate-200 bg-white">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-3.5 h-3.5 text-[#6B7280]" />
                          <p className="text-[11.5px] text-[#6B7280]">
                            {translateInterfaceText("E-mail PayPal cadastrado")}</p>
                        </div>
                        <p className="text-[13.5px] font-semibold text-[#0A1A2F] break-all select-all">
                          {selected.paymentAccount.key_paypal || '—'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-center">
                    <p className="text-[13px] font-medium text-amber-700">
                      {translateInterfaceText("⚠️ O usuário ainda não configurou uma conta padrão!")}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[13px] text-[#6B7280] text-center py-4">
                {translateInterfaceText("Selecione uma solicitação.")}</p>
            )}
          </div>

          <div className="h-px bg-[#EEF0F3]" />

          <DialogFooter className="px-6 py-4 flex sm:justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="h-10 rounded-xl border-[#E5EAF3] text-[#374151] bg-white hover:bg-[#F3F4F6] hover:text-[#131212]"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              disabled={!selected?.paymentAccount}
              onClick={() => {
                if (selected) {
                  setIsModalOpen(false);
                  navigate(`/updatepayment/${selected.id_payout}`);
                }
              }}
              className="h-10 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-sm gap-2 disabled:opacity-50"
            >
              {translateInterfaceText("Ir para pagamento")}<ArrowRight className="w-4 h-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
